import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { baseApi } from '../../../shared/api/baseApi';
import type { ApiPath } from '../../../shared/api/types';
import { getErrorStatus } from '../../../shared/api/errors';
import { notifyError } from '../../../shared/lib/notifications';
import { notifyOnError } from '../../../shared/api/onQueryError';
import { setToken } from '../../../shared/lib/token';
import { sessionImpersonated, sessionLoggedOut } from '../model/actions';
import { setRegions, setUser } from '../model/slice';
import type { LoginPayload, LoginResponse, Region, User } from '../model/types';
import { selectOrganization } from '../../organization/model/slice';
import { isAccessRestricted } from '../../../utils/isAccessRestricted';

export const sessionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginPayload>({
      query: (body) => ({
        url: '/api/login' satisfies ApiPath,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          setToken(data.token);
          dispatch(setUser(data.user));
        } catch (error) {
          const apiError = (error as { error?: unknown }).error;

          // Техработы: вместо тоста LoginPage покажет MaintenanceModal
          if (isAccessRestricted(apiError)) return;

          const status = getErrorStatus(apiError);
          notifyError(
            status === 401 || status === 403
              ? 'Неверная почта или пароль'
              : 'Не удалось войти. Попробуйте позже или обратитесь в поддержку',
          );
        }
      },
    }),

    resetPassword: builder.mutation<void, { email: string; url: string }>({
      query: (body) => ({
        url: '/api/users/send_password' satisfies ApiPath,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось отправить письмо');
      },
    }),

    changeLoginPassword: builder.mutation<void, { token: string; body: { email: string; password: string; repeat_password: string } }>({
      query: ({ token, body }) => ({
        url: `/api/users/reset_pass/${token}` satisfies ApiPath,
        method: 'POST',
        body,
      }),
      onQueryStarted: (_arg, { queryFulfilled }) => {
        void notifyOnError(queryFulfilled, 'Не удалось изменить пароль');
      },
    }),

    getCurrentUser: builder.query<User, void>({
      query: () => ({ url: '/api/users/current' satisfies ApiPath }),
      providesTags: ['Session'],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch {
          // 401
        }
      },
    }),

    getRegions: builder.query<Region[], void>({
      query: () => ({ url: '/api/projects/regions' satisfies ApiPath }),
      transformResponse: (response: { regions: Region[] }) => response.regions,
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setRegions(data));
        } catch {
          /* регионы не критичны для загрузки приложения */
        }
      },
    }),

    changePassword: builder.mutation<unknown, string>({
      query: (password) => ({
        url: '/api/users/change_password' satisfies ApiPath,
        method: 'PATCH',
        body: { password },
      }),
    }),

    loginAs: builder.mutation<LoginResponse, { userId: string; organizationId?: string | null }>({
      query: ({ userId }) => ({
        url: '/api/login/login_as',
        method: 'POST',
        body: { user_id: userId },
      }),
      onQueryStarted: async ({ organizationId }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        setToken(data.token);
        dispatch(setUser(data.user));
        dispatch(sessionImpersonated());
        if (organizationId) dispatch(selectOrganization(organizationId));
        dispatch(baseApi.util.resetApiState());
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useGetCurrentUserQuery,
  useGetRegionsQuery,
  useChangePasswordMutation,
  useChangeLoginPasswordMutation,
  useLoginAsMutation,
  useResetPasswordMutation,
} = sessionApi;

export const logout = () => (dispatch: Dispatch<UnknownAction>) => {
  dispatch(sessionLoggedOut());
  dispatch(baseApi.util.resetApiState());
};
